import { RevolutCurrencies } from '@constants';
import { relevantCurrencies } from '@utils/helpers';
import { ExpandMore as ChevronIcon } from '@material-ui/icons';
import React from 'react';
import { inputClass } from '../ProfileStep';

export const MIN_TIP = 2;

// Starting suggestions so nobody faces a 0 they must change before they can
// continue. 10 matches the guest page's own fallback when a guide has no
// default (TA-Tipping TippingPage/saga.js, defaultPaymentAmount = 1000 cents).
export const SUGGESTED = { bespoke: 10, good: 5, great: 10, amazing: 15 };

export const QUICK_KEYS = [
  { key: 'good', label: 'Good' },
  { key: 'great', label: 'Great' },
  { key: 'amazing', label: 'Amazing' }
];

const symbolFor = currency =>
  `${relevantCurrencies[currency] || RevolutCurrencies[currency] || ''}`.replace(/^[A-Z]{3}\s*/, '') ||
  `${currency || ''}`.toUpperCase();

/** Field errors for the amounts shown in `mode`, keyed like the inputs. */
export const tipErrors = (mode, amounts) => {
  const errors = {};
  const check = (key, value) => {
    const n = Number(value);
    if (value === '' || value === null || value === undefined || Number.isNaN(n)) {
      errors[key] = 'Enter an amount.';
    } else if (n < MIN_TIP) {
      errors[key] = `Enter at least ${MIN_TIP}.`;
    }
  };
  if (mode === 'quick') QUICK_KEYS.forEach(({ key }) => check(key, amounts[key]));
  else check('bespoke', amounts.bespoke);
  return errors;
};

const AmountInput = ({ id, label, value, onChange, error, symbol, hint }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold text-slate-700 mb-2">
      {label}
    </label>
    <div className="relative">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base text-slate-500 pointer-events-none">
        {symbol}
      </span>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min={MIN_TIP}
        step="0.5"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => e.key === 'e' && e.preventDefault()}
        aria-invalid={Boolean(error)}
        className={`${inputClass(error)} pl-10 tabular-nums`}
      />
    </div>
    {error ? (
      <p className="text-sm text-red-700 mt-1 mb-0">{error}</p>
    ) : (
      hint && <p className="text-sm text-slate-500 mt-1 mb-0">{hint}</p>
    )}
  </div>
);

/**
 * Onboarding last step. Currency plus the suggested tip, either one amount
 * ("bespoke") or three buttons guests choose between ("quick"); which of the
 * two a new guide sees is still decided by the onboarding_tipping_option_v1
 * experiment. Amounts start filled in with sensible suggestions.
 */
const TippingStep = ({ mode, currency, onCurrency, currencyLocked, amounts, onAmount, errors }) => {
  const options = { ...relevantCurrencies, ...(currencyLocked ? RevolutCurrencies : {}) };
  const symbol = symbolFor(currency);

  return (
    <div className="grid gap-7">
      <div>
        <label htmlFor="onboarding-currency" className="block text-sm font-semibold text-slate-700 mb-2">
          Currency
        </label>
        <div className="relative">
          <select
            id="onboarding-currency"
            value={currency}
            disabled={currencyLocked}
            onChange={e => onCurrency(e.target.value)}
            className={`${inputClass(false)} appearance-none pr-10 cursor-pointer disabled:cursor-default disabled:bg-slate-100`}>
            {Object.entries(options).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <ChevronIcon className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
        </div>
        {currencyLocked && (
          <p className="text-sm text-slate-500 mt-1 mb-0">Set by your Revolut account.</p>
        )}
      </div>

      {mode === 'quick' ? (
        <div>
          <p className="text-sm text-slate-600 mt-0 mb-3 max-w-[56ch]">
            Guests choose between these three amounts. Make Great the amount you would ideally like.
          </p>
          <div className="grid grid-cols-3 gap-3">
            {QUICK_KEYS.map(({ key, label }) => (
              <AmountInput
                key={key}
                id={`onboarding-tip-${key}`}
                label={label}
                value={amounts[key]}
                onChange={value => onAmount(key, value)}
                error={errors[key]}
                symbol={symbol}
              />
            ))}
          </div>
        </div>
      ) : (
        <AmountInput
          id="onboarding-tip-bespoke"
          label="Suggested tip"
          value={amounts.bespoke}
          onChange={value => onAmount('bespoke', value)}
          error={errors.bespoke}
          symbol={symbol}
          hint="The amount guests see first. Make it what you would ideally like to be paid."
        />
      )}
    </div>
  );
};

export default TippingStep;
